<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="Sistem"
      title="Log Explorer"
      description="Aktif backend log dosyasini filtreleyin, son kayitlari izleyin ve secilen kaydin ham detayini inceleyin."
    >
      <template #actions>
        <div class="d-flex align-center flex-wrap log-explorer__header-actions">
          <v-switch
            v-model="live"
            dense
            inset
            hide-details
            class="mt-0 pt-0 mr-4"
            label="Canli yenile"
          ></v-switch>

          <v-btn
            color="primary"
            small
            :loading="loading || rawLoading"
            @click="refreshAll"
          >
            <v-icon left>
              {{ icons.mdiRefresh }}
            </v-icon>
            Yenile
          </v-btn>
        </div>
      </template>
    </admin-page-header>

    <v-alert
      v-if="errorMessage"
      text
      dense
      type="error"
      class="mb-6"
    >
      {{ errorMessage }}
    </v-alert>

    <admin-metric-strip
      :items="summaryCards"
      :loading="loading"
    />

    <v-card
      class="admin-table-card mb-6"
      outlined
    >
      <v-card-text class="pb-4">
        <admin-saved-filters
          storage-key="system-logs"
          :value="filters"
          class="mb-4"
          @apply="restoreSavedFilters"
        />
        <v-row dense>
          <v-col
            cols="12"
            md="3"
          >
            <v-select
              v-model="filters.level"
              :items="levelOptions"
              label="Seviye"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>

          <v-col
            cols="12"
            md="5"
          >
            <v-text-field
              v-model.trim="filters.search"
              outlined
              dense
              clearable
              hide-details
              label="Mesaj, logger, thread veya correlation ID"
              @keydown.enter.prevent="applyFilters"
            ></v-text-field>
          </v-col>

          <v-col
            cols="12"
            md="2"
          >
            <v-select
              v-model="filters.limit"
              :items="limitOptions"
              label="Limit"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>

          <v-col
            cols="12"
            md="2"
            class="d-flex align-center"
          >
            <v-btn
              color="primary"
              small
              block
              :loading="loading"
              @click="applyFilters"
            >
              Uygula
            </v-btn>
          </v-col>
        </v-row>

        <v-row
          dense
          class="mt-3"
        >
          <v-col
            cols="12"
            sm="6"
          >
            <v-text-field
              v-model="filters.from"
              type="datetime-local"
              label="Başlangıç (log saati)"
              outlined
              dense
              hide-details
            />
          </v-col><v-col
            cols="12"
            sm="6"
          >
            <v-text-field
              v-model="filters.to"
              type="datetime-local"
              label="Bitiş (log saati)"
              outlined
              dense
              hide-details
            />
          </v-col>
        </v-row>
        <div class="text-caption mt-2">
          Kapsam: geçerli log dosyasının son 5.000–10.000 satırı. Arşiv dosyaları dahil değil.
        </div>
        <div class="d-flex align-center justify-space-between flex-wrap mt-4">
          <div class="text-caption text--secondary">
            Kaynak: {{ logSourceLabel }}
          </div>

          <v-btn
            text
            small
            color="primary"
            @click="clearFilters"
          >
            Filtreleri Temizle
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-card
      class="admin-table-card"
      outlined
    >
      <v-tabs
        v-model="activeTab"
        class="admin-tabs"
      >
        <v-tab>Log Akisi</v-tab>
        <v-tab>Raw Tail</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <v-card-text class="pa-6">
            <v-card
              class="admin-surface log-stream-card"
              outlined
            >
              <v-progress-linear
                v-if="loading"
                indeterminate
                color="primary"
              ></v-progress-linear>

              <v-card-text class="pa-0">
                <div
                  v-if="!entries.length && !loading"
                  class="pa-6"
                >
                  <v-alert
                    text
                    dense
                    color="info"
                    class="mb-0"
                  >
                    Secili filtrelere uyan log kaydi bulunamadi.
                  </v-alert>
                </div>

                <div
                  v-for="entry in entries"
                  :key="entry.id"
                  class="log-line-wrapper"
                >
                  <div
                    class="log-line"
                    :class="{ 'log-line--active': entry.id === selectedEntryId }"
                    :style="{ borderLeftColor: levelColor(entry.level) }"
                    @click="selectEntry(entry)"
                  >
                    <span class="log-line__time">
                      {{ formatDateTime(entry.timestamp, true) }}
                    </span>

                    <span
                      class="log-line__badge"
                      :style="{ color: levelColor(entry.level), backgroundColor: levelSoftColor(entry.level) }"
                    >
                      {{ entry.level }}
                    </span>

                    <span class="log-line__logger">
                      {{ shortLoggerName(entry.loggerName) }}
                    </span>

                    <span class="log-line__message">
                      {{ entry.message }}
                    </span>

                    <span class="log-line__meta">
                      {{ entry.threadName || '-' }}
                    </span>

                    <span
                      v-if="entry.hasStackTrace"
                      class="log-line__stack"
                    >
                      stack
                    </span>
                  </div>

                  <div
                    v-if="entry.id === selectedEntryId"
                    class="log-inline-detail"
                  >
                    <v-progress-linear
                      v-if="detailLoading"
                      indeterminate
                      color="primary"
                    ></v-progress-linear>

                    <div class="log-inline-detail__header">
                      <div class="font-weight-bold">
                        Log Detayı
                      </div>

                      <v-btn
                        icon
                        small
                        @click="clearSelection"
                      >
                        <v-icon>{{ icons.mdiClose }}</v-icon>
                      </v-btn>
                    </div>

                    <v-alert
                      v-if="detailError"
                      text
                      dense
                      type="error"
                      class="mb-4"
                    >
                      {{ detailError }}
                    </v-alert>

                    <template v-else-if="selectedDetail.entry">
                      <div class="log-detail__meta-grid mb-4">
                        <div class="log-detail__meta">
                          <span>Seviye</span>
                          <strong :style="{ color: levelColor(selectedDetail.entry.level) }">
                            {{ selectedDetail.entry.level }}
                          </strong>
                        </div>

                        <div class="log-detail__meta">
                          <span>Zaman</span>
                          <strong>{{ formatDateTime(selectedDetail.entry.timestamp) }}</strong>
                        </div>

                        <div class="log-detail__meta">
                          <span>Thread</span>
                          <strong>{{ selectedDetail.entry.threadName || '-' }}</strong>
                        </div>

                        <div class="log-detail__meta">
                          <span>Logger</span>
                          <strong>{{ selectedDetail.entry.loggerName || '-' }}</strong>
                        </div>
                      </div>

                      <div class="text-caption text--secondary mb-2">
                        {{ selectedDetail.entry.lineCount }} satır
                      </div>

                      <pre class="log-code">{{ selectedDetail.raw }}</pre>
                    </template>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-tab-item>

        <v-tab-item>
          <v-card-text class="pa-6">
            <div class="d-flex align-center justify-space-between flex-wrap mb-4">
              <div class="d-flex align-center flex-wrap raw-toolbar">
                <v-select
                  v-model="filters.rawLines"
                  :items="rawLineOptions"
                  label="Satir"
                  outlined
                  dense
                  hide-details
                  class="raw-toolbar__select mr-3"
                ></v-select>

                <v-btn
                  color="primary"
                  small
                  :loading="rawLoading"
                  @click="fetchRawTail"
                >
                  <v-icon left>
                    {{ icons.mdiRefresh }}
                  </v-icon>
                  Raw Tail Yenile
                </v-btn>
              </div>

              <div class="text-caption text--secondary mt-2 mt-md-0">
                Aktif dosyanin son {{ filters.rawLines }} satiri
              </div>
            </div>

            <v-alert
              v-if="rawError"
              text
              dense
              type="error"
              class="mb-4"
            >
              {{ rawError }}
            </v-alert>

            <v-card
              class="admin-surface"
              outlined
            >
              <v-progress-linear
                v-if="rawLoading"
                indeterminate
                color="primary"
              ></v-progress-linear>

              <v-card-text class="pa-0">
                <pre class="log-code log-code--tail">{{ rawTail || 'Raw tail verisi bulunamadi.' }}</pre>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios'
import { mdiClose, mdiRefresh } from '@mdi/js'
import { getAccessToken } from '@/utils/panelSession'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminSavedFilters from '@/components/admin/AdminSavedFilters.vue'
import AdminMetricStrip from '@/components/admin/AdminMetricStrip.vue'
import { parseAdminDate, formatAdminDate } from '@/utils/adminDate'

const LIVE_REFRESH_MS = 15000

const defaultListPayload = () => ({
  fileName: '',
  filePath: '',
  fileSizeBytes: 0,
  modifiedAt: null,
  limit: 100,
  level: null,
  search: null,
  totalMatched: 0,
  displayedCount: 0,
  stackTraceCount: 0,
  traceCount: 0,
  debugCount: 0,
  infoCount: 0,
  warnCount: 0,
  errorCount: 0,
  entries: [],
})

const defaultDetailPayload = () => ({
  fileName: '',
  filePath: '',
  modifiedAt: null,
  entry: null,
  raw: '',
})

const defaultRawPayload = () => ({
  fileName: '',
  filePath: '',
  modifiedAt: null,
  lines: 250,
  raw: '',
})

export default {
  name: 'SystemLogExplorer',
  components: {
    AdminPageHeader,
    AdminSavedFilters,
    AdminMetricStrip,
  },
  data() {
    return {
      activeTab: 0,
      loading: false,
      detailLoading: false,
      rawLoading: false,
      errorMessage: '',
      detailError: '',
      rawError: '',
      live: true,
      liveTimer: null,
      filters: {
        from: '',
        to: '',
        level: 'ALL',
        search: '',
        limit: 100,
        rawLines: 250,
      },
      levelOptions: ['ALL', 'ERROR', 'WARN', 'INFO', 'DEBUG', 'TRACE'],
      limitOptions: [50, 100, 150, 250],
      rawLineOptions: [100, 250, 500, 1000, 1500],
      logList: defaultListPayload(),
      selectedEntryId: null,
      selectedDetail: defaultDetailPayload(),
      rawTailPayload: defaultRawPayload(),
      icons: {
        mdiClose,
        mdiRefresh,
      },
    }
  },
  computed: {
    requestOptions() {
      return {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    },
    entries() {
      return this.logList.entries || []
    },
    rawTail() {
      return this.rawTailPayload.raw || ''
    },
    logSourceLabel() {
      if (!this.logList.filePath) {
        return 'Log dosyasi henuz algilanmadi.'
      }

      return `${this.logList.fileName || 'spring.log'} • ${this.formatFileSize(this.logList.fileSizeBytes)} • ${this.formatDateTime(this.logList.modifiedAt)}`
    },
    summaryCards() {
      return [
        {
          label: 'Eslesen Kayit',
          value: this.formatNumber(this.logList.totalMatched),
          helper: `${this.formatNumber(this.logList.displayedCount)} kayit ekranda`,
          color: '#0d6efd',
        },
        {
          label: 'Error',
          value: this.formatNumber(this.logList.errorCount),
          helper: 'Filtrelenmis son kayitlar',
          color: '#FF4C51',
        },
        {
          label: 'Warn',
          value: this.formatNumber(this.logList.warnCount),
          helper: 'Filtrelenmis son kayitlar',
          color: '#FFB400',
        },
        {
          label: 'Info',
          value: this.formatNumber(this.logList.infoCount),
          helper: 'Filtrelenmis son kayitlar',
          color: '#16B1FF',
        },
        {
          label: 'Stack Trace',
          value: this.formatNumber(this.logList.stackTraceCount),
          helper: 'Birden fazla satir',
          color: '#8A8D93',
        },
        {
          label: 'Guncellendi',
          value: this.formatRelativeTime(this.logList.modifiedAt),
          helper: this.logList.fileName || 'Kaynak dosya yok',
          color: '#56CA00',
        },
      ]
    },
  },
  watch: {
    live(value) {
      if (value) {
        this.startLiveRefresh()

        return
      }

      this.stopLiveRefresh()
    },
    activeTab(value) {
      if (value === 1 && !this.rawTail && !this.rawLoading) {
        this.fetchRawTail()
      }
    },
  },
  mounted() {
    this.bootstrap()
  },
  beforeDestroy() {
    this.stopLiveRefresh()
  },
  methods: {
    restoreSavedFilters(value) { this.filters = { ...this.filters, ...value }; this.applyFilters() },
    async bootstrap() {
      await this.fetchLogs(false)
      await this.fetchRawTail()
      this.startLiveRefresh()
    },
    startLiveRefresh() {
      this.stopLiveRefresh()
      if (!this.live) {
        return
      }

      this.liveTimer = window.setInterval(() => {
        this.handleLiveRefresh()
      }, LIVE_REFRESH_MS)
    },
    stopLiveRefresh() {
      if (this.liveTimer) {
        window.clearInterval(this.liveTimer)
        this.liveTimer = null
      }
    },
    async handleLiveRefresh() {
      if (!this.live || this.loading || this.detailLoading || this.rawLoading) {
        return
      }

      await this.fetchLogs(true)

      if (this.activeTab === 1) {
        await this.fetchRawTail()
      }
    },
    async refreshAll() {
      await this.fetchLogs(true)
      await this.fetchRawTail()
    },
    async applyFilters() {
      await this.fetchLogs(false)
    },
    async clearFilters() {
      this.filters.from = ''
      this.filters.to = ''
      this.filters.level = 'ALL'
      this.filters.search = ''
      this.filters.limit = 100
      await this.fetchLogs(false)
    },
    async fetchLogs(preserveSelection = true) {
      if (this.filters.from && this.filters.to && this.filters.from > this.filters.to) {
        this.errorMessage = 'Başlangıç tarihi bitişten sonra olamaz.'

        return
      }
      this.loading = true
      this.errorMessage = ''

      const params = {
        limit: this.filters.limit,
        from: this.filters.from || undefined,
        to: this.filters.to || undefined,
      }

      if (this.filters.level && this.filters.level !== 'ALL') {
        params.level = this.filters.level
      }
      if (this.filters.search) {
        params.search = this.filters.search
      }

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/logs`,
          {
            ...this.requestOptions,
            params,
          },
        )

        const payload = response?.data?.data || defaultListPayload()
        this.logList = {
          ...defaultListPayload(),
          ...payload,
          entries: Array.isArray(payload.entries) ? payload.entries : [],
        }

        if (!this.logList.entries.length) {
          this.selectedEntryId = null
          this.selectedDetail = defaultDetailPayload()

          return
        }

        const nextSelectedId = preserveSelection && this.selectedEntryId && this.logList.entries.some(item => item.id === this.selectedEntryId)
          ? this.selectedEntryId
          : this.logList.entries[0].id

        const shouldReloadDetail = !this.selectedDetail.entry || this.selectedDetail.entry.id !== nextSelectedId
        this.selectedEntryId = nextSelectedId

        if (shouldReloadDetail) {
          await this.fetchEntryDetail(nextSelectedId)
        }
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Log kayitlari yuklenemedi.'
        console.error('Log kayitlari yuklenirken hata olustu:', error)
      } finally {
        this.loading = false
      }
    },
    async selectEntry(entry) {
      if (!entry?.id) {
        return
      }
      if (this.selectedEntryId === entry.id && this.selectedDetail.entry?.id === entry.id) {
        this.clearSelection()

        return
      }

      this.selectedEntryId = entry.id
      await this.fetchEntryDetail(entry.id)
    },
    clearSelection() {
      this.selectedEntryId = null
      this.selectedDetail = defaultDetailPayload()
      this.detailError = ''
    },
    async fetchEntryDetail(entryId) {
      this.detailLoading = true
      this.detailError = ''

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/logs/${entryId}`,
          this.requestOptions,
        )

        const payload = response?.data?.data || defaultDetailPayload()
        this.selectedDetail = {
          ...defaultDetailPayload(),
          ...payload,
        }
      } catch (error) {
        this.detailError = error?.response?.data?.message || 'Log detayi yuklenemedi.'
        this.selectedDetail = defaultDetailPayload()
        console.error('Log detayi yuklenirken hata olustu:', error)
      } finally {
        this.detailLoading = false
      }
    },
    async fetchRawTail() {
      this.rawLoading = true
      this.rawError = ''

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/logs/raw`,
          {
            ...this.requestOptions,
            params: {
              lines: this.filters.rawLines,
            },
          },
        )

        const payload = response?.data?.data || defaultRawPayload()
        this.rawTailPayload = {
          ...defaultRawPayload(),
          ...payload,
        }
      } catch (error) {
        this.rawError = error?.response?.data?.message || 'Raw tail yuklenemedi.'
        console.error('Raw tail yuklenirken hata olustu:', error)
      } finally {
        this.rawLoading = false
      }
    },
    levelColor(level) {
      return ({
        ERROR: '#FF4C51',
        WARN: '#FFB400',
        INFO: '#16B1FF',
        DEBUG: '#8A8D93',
        TRACE: '#7c4dff',
      })[level] || '#8A8D93'
    },
    levelSoftColor(level) {
      return ({
        ERROR: 'rgba(255, 76, 81, 0.14)',
        WARN: 'rgba(255, 180, 0, 0.16)',
        INFO: 'rgba(22, 177, 255, 0.16)',
        DEBUG: 'rgba(138, 141, 147, 0.16)',
        TRACE: 'rgba(124, 77, 255, 0.12)',
      })[level] || 'rgba(138, 141, 147, 0.16)'
    },
    shortLoggerName(value) {
      if (!value) {
        return '-'
      }

      const trimmed = String(value).trim()
      const parts = trimmed.split('.')

      return parts[parts.length - 1] || trimmed
    },
    formatNumber(value) {
      return new Intl.NumberFormat('tr-TR').format(Number(value || 0))
    },
    formatFileSize(value) {
      const size = Number(value || 0)
      if (!size) return '0 B'
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
      if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`

      return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`
    },
    formatDateTime(value, compact = false) {
      return formatAdminDate(value, compact)
    },
    formatRelativeTime(value) {
      if (!value) {
        return '-'
      }

      const parsed = parseAdminDate(value)
      if (!parsed) {
        return '-'
      }

      const diffMs = Date.now() - parsed.getTime()
      const diffMinutes = Math.floor(diffMs / 60000)

      if (diffMinutes < 1) return 'Simdi'
      if (diffMinutes < 60) return `${diffMinutes} dk once`

      const diffHours = Math.floor(diffMinutes / 60)
      if (diffHours < 24) return `${diffHours} sa once`

      const diffDays = Math.floor(diffHours / 24)

      return `${diffDays} gun once`
    },
  },
}
</script>

<style lang="scss" scoped>
.log-explorer__header-actions {
  gap: 12px;
}

.log-summary-card {
  min-height: 138px;
}

.log-stream-card,
.log-detail-card {
  height: 100%;
}

.log-line-wrapper {
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
}

.log-line {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-left: 3px solid transparent;
  cursor: pointer;
  transition: background 0.2s ease;
}

.log-line:hover {
  background: rgba(148, 163, 184, 0.05);
}

.log-line--active {
  background: rgba(13, 110, 253, 0.06);
}

.log-inline-detail {
  padding: 16px;
  background: rgba(148, 163, 184, 0.06);
}

.log-inline-detail__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.log-line__time,
.log-line__meta {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: rgba(148, 163, 184, 0.92);
  flex-shrink: 0;
}

.log-line__time {
  width: 84px;
}

.log-line__badge,
.log-line__stack {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}

.log-line__stack {
  background: rgba(148, 163, 184, 0.12);
  color: rgba(15, 23, 42, 0.78);
}

.log-line__logger {
  min-width: 96px;
  max-width: 120px;
  font-size: 11px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-line__message {
  font-size: 12px;
  min-width: 0;
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.log-detail__meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.log-detail__meta {
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(148, 163, 184, 0.08);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.log-detail__meta span {
  font-size: 11px;
  color: rgba(148, 163, 184, 0.96);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.log-code {
  margin: 0;
  border-radius: 16px;
  padding: 16px;
  background: rgba(15, 23, 42, 0.92);
  color: #e2e8f0;
  font-size: 11px;
  line-height: 1.6;
  font-family: 'JetBrains Mono', monospace;
  overflow: auto;
  max-height: 540px;
}

.log-code--tail {
  border-radius: 0 0 16px 16px;
  min-height: 540px;
}

.raw-toolbar {
  gap: 12px;
}

.raw-toolbar__select {
  max-width: 132px;
}

@media (max-width: 1264px) {
  .log-line {
    flex-wrap: wrap;
  }

  .log-line__message {
    order: 10;
    flex-basis: 100%;
  }
}

@media (max-width: 960px) {
  .log-detail__meta-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
